// The hall-screen URL this sign-in is using, and why that is not a second session.
//
// THE ADMIN TOKEN IS NOT HERE. session/store.ts is the only writer of the
// session. This file writes a second key in the same kind of store: a map of
// activation id to the last view URL minted or pasted in this tab, keyed by
// `sessionId`. A stolen copy of this list is a rotation a hall screen would
// play. It does not let anybody publish, mint, or read a report. The session
// already in this store can mint another view token, so remembering the URL
// next to it does not enlarge what an attacker who can read the store can do.
//
// WHY THIS IS NOT MEMORY ALONE. A URL that died on every reload made people
// paste the token back in, or keep it in a note. That is a worse leak than
// the same tab store the session already accepted. The mint response is still
// shown once on Schedule; this is the constructed hall URL for the rest of
// the sitting, so Content can keep "Test this piece" after a refresh.
//
// Same tab store as the session, never the origin-wide one. A fresh login is
// a new `sessionId` and the URLs are gone. Signing out removes the key with
// the session. A reload of the same tab keeps them, which is the whole of
// what remembering them is for.
//
// THE STORE IS HANDED IN. Reaching for the browser object from here would be
// a second file that knows where the session lives, and check.mjs names
// store.ts as the only one that may. This module reads and writes a key.
//
// THE SHAPE IS VALIDATED RATHER THAN CAST. The value came out of a store a
// person can edit. A missing `sessionId`, a href that is not a complete hall
// URL, or an activation that does not match the fragment all read as nothing
// remembered.

import { screenHrefLooksComplete } from "../kiosk/link.js";
import { KIOSK_VIEW_KEY, type KeyedStore } from "./store.js";

export { KIOSK_VIEW_KEY };

type Held = {
  readonly sessionId: string;
  readonly hrefs: Readonly<Record<string, string>>;
};

function parseHeld(raw: string): Held | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  const sessionId = row["sessionId"];
  const hrefs = row["hrefs"];
  if (typeof sessionId !== "string" || sessionId === "") return null;
  if (typeof hrefs !== "object" || hrefs === null || Array.isArray(hrefs)) return null;
  const next: Record<string, string> = {};
  for (const [activationId, href] of Object.entries(hrefs)) {
    if (typeof href !== "string" || !hrefForActivation(href, activationId)) continue;
    next[activationId] = href;
  }
  return { sessionId, hrefs: next };
}

function hrefForActivation(href: string, activationId: string): boolean {
  if (!screenHrefLooksComplete(href)) return false;
  const hashAt = href.indexOf("#");
  if (hashAt < 0) return false;
  const found = new URLSearchParams(href.slice(hashAt + 1));
  return (found.get("activation") ?? "").trim() === activationId;
}

function readHeld(sessionId: string, storage: KeyedStore): Held {
  let raw: string | null;
  try {
    raw = storage.getItem(KIOSK_VIEW_KEY);
  } catch {
    return { sessionId, hrefs: {} };
  }
  if (raw === null) return { sessionId, hrefs: {} };
  const held = parseHeld(raw);
  if (held === null || held.sessionId !== sessionId) return { sessionId, hrefs: {} };
  return held;
}

/** The hall URL this sign-in last used for this activation, or null. */
export function readKioskViewHref(sessionId: string, activationId: string, storage: KeyedStore): string | null {
  const href = readHeld(sessionId, storage).hrefs[activationId];
  return href === undefined ? null : href;
}

/** Remember a complete hall URL for this sign-in and this activation. */
export function writeKioskViewHref(
  sessionId: string,
  activationId: string,
  href: string,
  storage: KeyedStore,
): boolean {
  if (!hrefForActivation(href, activationId)) return false;
  try {
    const held = readHeld(sessionId, storage);
    storage.setItem(
      KIOSK_VIEW_KEY,
      JSON.stringify({ sessionId, hrefs: { ...held.hrefs, [activationId]: href } }),
    );
  } catch {
    // The URL is still in React state for this mount.
  }
  return true;
}

/** Forget every hall URL this tab was holding. */
export function clearKioskViewHrefs(storage: KeyedStore): void {
  try {
    storage.removeItem(KIOSK_VIEW_KEY);
  } catch {
    // Same as a session the browser will not forget: the in-memory URL dies with the page.
  }
}
